import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonSpinner, IonTitle, IonToolbar } from "@ionic/react";
import { AppSettings }  from "components/AppSettings";
import { Glob, useLoc } from "functions/Language";
import { DeviceInfo, IsDeviceInfo, SupportedDevices } from "functions/networking/NetTypes";
import { RouterMap } from "navigation/RouterMap";
import { MqttClient } from "mqtt/dist/mqtt";
import { FC, useEffect, useReducer, useRef } from "react";

import cfg from "config.json";
import { reloadOutline } from "ionicons/icons";


const PRESENCE_PERIOD = 5e3;

async function KeepPresence(mqttClient: MqttClient, broadcast: string, stop: { val: boolean }) {
	while (!stop.val) {
		const err = await new Promise<Error | undefined>(res => mqttClient.publish(broadcast, '  {"Command": "Presence"}', {}, res));
		if (err !== undefined) {
			console.error(err);
			break;
		}

		await new Promise(res => setTimeout(res, PRESENCE_PERIOD));
	}
}

function useMqttPresence(client: MqttClient, broadcast: string, reload: unknown) {
	const devices = useRef<DeviceInfo<SupportedDevices>[]>([]);
	const [gen, redraw] = useReducer((v : number) => v + 1, 0);

	useEffect(() => {
		const stop = { val: false };

		devices.current = [];
		redraw();

		function HandleResponse(topic: string, payload: Buffer) {
			if (topic !== broadcast) return;

			let o: any;
			try {
				o = JSON.parse(payload.toString());
			}
			catch (e) {
				console.warn("Failed to resolve device presence.", e);
				return;
			}

			if (o.File_type === "Presence" && o["Device"] !== undefined && IsDeviceInfo(o.Device)) {
				if (devices.current.every(d => d.Mqtt_uid !== o.Device.Mqtt_uid)) {
					devices.current.push(o.Device);
					devices.current.sort((a, b) => a.Mqtt_uid < b.Mqtt_uid ? -1 : 1);
					redraw();
				}
			}
		}

		client.on("message", HandleResponse);

		KeepPresence(client, broadcast, stop);

		return () => {
			stop.val = true;
			client.removeListener("message", HandleResponse);
		};
	}, [client, broadcast, reload]);

	return {
		list:       devices.current,
		generation: gen,
	}
}


export const DevicesPage : FC<{
  setDevice: (dvc: DeviceInfo<SupportedDevices> | null) => void,
	mqtt: MqttClient,
	broadcastTopic: string,
}> = (props) => {
  const loc = useLoc(glob);
	const [reloadDep, doReload] = useReducer((v : number) => v + 1, 0);
	const devices = useMqttPresence(props.mqtt, props.broadcastTopic, reloadDep);

  return <IonPage>
    <IonHeader>
      <IonToolbar color="tertiary">
        <IonTitle>{loc.title}</IonTitle>
        <IonButtons slot="end">
          <IonButton onClick={doReload}>
            <IonIcon icon={reloadOutline} />
          </IonButton>
          <AppSettings />
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonList>
        {devices.list.map((dvc, i) => <IonItem
            key={i}
            onClick={(ev) => {
              if (cfg.DEV) ev.preventDefault();
              props.setDevice(dvc);
            }}
            routerLink={RouterMap.overview}
            routerDirection="root"
          >
          <IonLabel className="sc-ion-label-md-s">
            <h2>{dvc.Description}</h2>
            <h3>{dvc.Name}</h3>
          </IonLabel>
        </IonItem>)}
      </IonList>
      {devices.list.length === 0 &&
        <IonItem>
          <IonSpinner />
          <IonLabel>&nbsp;{loc.loading}</IonLabel>
        </IonItem>}
    </IonContent>
  </IonPage>
}

const glob = Glob({
	en: {
		logout: "Log out",
		loading: "Searching for devices...",
		title: "Device selection",
		no_devices: "No devices found.",
	},
	cs: {
		logout: "Odhlásit",
		loading: "Hledání přístrojů...",
		title: "Výběr zařízení",
		no_devices: "Nebyl nalezen žádný přístoj."
	},
	pg: {
		logout: "Sair",
		loading: "Busca de dispositivos...",
		title: "Seleção de dispositivos",
		no_devices: "Nenhum dispositivo encontrado",
	},
	es: {
		logout: "Cerrar sesión",
		loading: "Cargando",
		title: "Selección de dispositivos",
		no_devices: "No se encuentran dispositivos",
	},
} as const);
